import { IEmailMessagingRepository } from '@/app/core/IRepositories/IEmailMessagingRepository';
import { ITenantRepository } from '@/app/core/IRepositories/ITenantRepository';
import { InternalChannel } from '@/app/core/Models/Channel';
import { ChannelType } from '@/app/core/Models/ChannelTypes';
import { RecentEmailInteraction, ThreadEmailInteraction } from '@/app/core/Models/email-interaction';
import { FileModel } from '@/app/core/Models/file';
import { Inbox } from '@/app/core/Models/inbox';
import { InteractionStatus } from '@/app/core/Models/Interaction';
import { EmailMessageModel } from '@/app/core/Models/Message';
import { RecentEmailsQueryDto, ThreadEmailsQueryDto } from '@/app/Data/DTO/emailMessagingDto';
import { AppState } from '@/app/State/AppState';
import { FetchEmailInteractionsAction, FetchRecentEmailsAction, SelectEmailClassificationAction, SelectEmailInboxAction, SendEmailNoteAction, SendReplyEmailAction, UpdateEmailReadStatusAction } from '@/app/State/email-inbox/email-inbox.action';
import { selectActiveConversationState, selectEmailClassifications, selectEmailInboxState, selectIsFetchingRecentEmails, selectRecentEmailsState, selectSelectedEmailInbox } from '@/app/State/email-inbox/email-inbox.selector';
import {
  InboxSelectionAction
} from '@/app/State/Inbox/inbox.action';
import {
  selectInboxes
} from '@/app/State/Inbox/inbox.selector';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  combineLatest,
  distinctUntilChanged, filter, firstValueFrom, map, of, shareReplay,
  switchMap,
  tap
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class EmailMessagingViewModel {


  emailInboxState$ = this.store
    .select(selectEmailInboxState)
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  isFetchingRecentEmails$ = this.store
    .select(selectIsFetchingRecentEmails)
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  inboxes$ = this.store
    .select(selectInboxes)
    .pipe(
      map(inboxes => [...inboxes].sort((a, b) => !a.is_visible ? 1 : !b.is_visible ? -1 : 0)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  selectedEmailInbox$ = this.store
    .select(selectSelectedEmailInbox)

  emailClassifications$ = this.store
    .select(selectEmailClassifications)

  recentEmailsState$ = this.store
    .select(selectRecentEmailsState)
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  activeConversationState$ = this.store
    .select(selectActiveConversationState)
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  activeRecentEmailConversation$ = this.activeConversationState$.pipe(
    map(state => state?.conversationId),
    distinctUntilChanged(),
    switchMap((conversationId) =>
      this.emailMessagingRepository.findRecentEmailMessage(conversationId || 0),
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  )


  recentEmailMessages$ = combineLatest([
    this.recentEmailsState$,
    this.selectedEmailInbox$
  ]).pipe(
    distinctUntilChanged(([state1, inbox1], [state2, inbox2]) =>
      state1?.emailClassificationId == state2?.emailClassificationId &&
      inbox1?.inbox_id == inbox2?.inbox_id &&
      state1?.page === state2?.page
    ),
    switchMap(([state, inbox]) => inbox && state?.page
      ? this.emailMessagingRepository.getLocalRecentMessages({
        classification_id: state.emailClassificationId,
        channelId: inbox.inbox_id,
        page: state.page,
        page_size: 20,
      })
      : of([])
    )
  )

  threadEmailInteractions$ = this.activeConversationState$.pipe(
    map((state) => ({ conversationId: state?.conversationId, page: state?.page })),
    filter(({ conversationId, page }) => !!conversationId && !!page),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    switchMap(({ conversationId, page }) =>
      this.emailMessagingRepository.getLocalThreadMessages({
        conversation_id: conversationId!,
        page: page!,
        page_size: 20,
      }).pipe(
        tap(messages => this.updateReadStatus(messages))
      )
    )
  );

  emailInternalChannels$ = this.tenantRepository.getLocalInternalChannels().pipe(
    map((internalChannels) => internalChannels.filter(channel => [ChannelType.Google, ChannelType.Outlook].includes(channel.channel_type))),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  constructor(
    private store: Store<AppState>,
    private emailMessagingRepository: IEmailMessagingRepository,
    private tenantRepository: ITenantRepository,
    private router: Router
  ) { }

  selectEmailClassification(classificationId: number) {
    this.store.dispatch(SelectEmailClassificationAction({ payload: { classificationId } }))
  }

  openEmailConversationThread(interaction: RecentEmailInteraction): void {
    this.router.navigate(['/main/email/conversation'], { queryParams: { id: interaction.conversation_id } });
    // this.store.dispatch(FetchEmailInteractionsAction({ payload: { query: { conversation_id: interaction.conversation_id as any, page: 1, page_size: 20, version: interaction.version } } }));
  }

  selectInbox(inbox?: Inbox) {
    inbox && [ChannelType.Outlook, ChannelType.Google].includes(inbox?.channel_type as any)
      ? this.store.dispatch(SelectEmailInboxAction({ payload: { inbox } }))
      : this.store.dispatch(InboxSelectionAction({ payload: { inbox } }));
  }

  fetchRecentEmails(query: RecentEmailsQueryDto) {
    this.store.dispatch(FetchRecentEmailsAction({ payload: { query } }))
  }

  fetchEmailInteractions(query: ThreadEmailsQueryDto) {
    this.store.dispatch(FetchEmailInteractionsAction({ payload: { query } }))
  }

  SendReplyEmail(data: EmailMessageModel, channel: InternalChannel) {
    const form = new FormData()
    form.append('content', data.content);
    form.append('email_from', data.email_from);
    form.append('email_id', data.email_id);
    form.append('subject', data.subject);
    data.addresses.forEach(x => form.append('addresses', x));
    data.cc_recipients.forEach(x => form.append('cc_recipients', x));
    data.bcc_recipients.forEach(x => form.append('bcc_recipients', x));
    data.attachments.forEach(x => form.append('attachments', x));
    [ChannelType.Outlook, ChannelType.Google].includes(channel.channel_type) && this.store.dispatch(SendReplyEmailAction({ payload: { data: form, channelType: channel.channel_type as any } }))
  }

  async sendInlineNote(data: { content: string; attachments: FileModel[] }) {
    const activeConversationState = await firstValueFrom(this.activeConversationState$)
    this.store.dispatch(SendEmailNoteAction({ payload: { content: data.content, conversationId: activeConversationState!.conversationId! } }))
  }

  async updateReadStatus(messages: ThreadEmailInteraction[]) {
    const message = messages.find((interaction) => [InteractionStatus.received, InteractionStatus.unread].includes(interaction.status))
    if (message) {
      const inbox = await firstValueFrom(this.selectedEmailInbox$);
      this.store.dispatch(UpdateEmailReadStatusAction({ payload: { channelId: inbox!.inbox_id, conversation_id: message.conversation_id, version: message.version } }));
    }
  }

  getActiveConverstaion(conversation_id: number) {
    return this.emailMessagingRepository.findRecentEmailMessage(conversation_id);
  }
}